import AssetSystem from '../systems/AssetSystem';
import {
	CREATE_ASSETS_BATCH_FROM_DIRECT_UPLOAD_START,
	CREATE_ASSETS_BATCH_FROM_DIRECT_UPLOAD_FINISH
} from '../actions/types';
import QueryClientUpdater from '../new_arch/services/QueryClientUpdater';

export const BATCH_SIZE = 50;
export const FLUSH_DEBOUNCE_MS = 3000;
const MAX_CONCURRENT_BATCHES = 3;

const bufferKey = ( projectID, folderID ) => `${projectID}:${folderID ?? 'root'}`;

export default class AssetBatchCreator {
	constructor( store, onBatchComplete ) {
		this.store = store;
		this.onBatchComplete = onBatchComplete;
		this.assetSystem = new AssetSystem( store.dispatch );

		this.buffers = new Map();
		this.flushTimers = new Map();
		this.pendingBatches = [];
		this.inFlightCount = 0;
	}

	add( { uploadID, signedId, projectID, folderID, parentUploadsData, parentUploadID, localFolderName } ) {
		const key = bufferKey( projectID, folderID );
		if ( !this.buffers.has( key ) ) {
			this.buffers.set( key, [] );
		}

		const buffers = this.buffers.get( key );
		buffers.push( {
			uploadID,
			signedId,
			parentUploadsData,
			parentUploadID,
			localFolderName
		} );

		if ( buffers.length >= BATCH_SIZE ) {
			this._flushBuffer( key );
		} else {
			this._scheduleFlush( key );
		}
	}

	_scheduleFlush( key ) {
		if ( this.flushTimers.has( key ) ) {
			clearTimeout( this.flushTimers.get( key ) );
		}

		const timer = setTimeout( () => {
			this.flushTimers.delete( key );
			this._flushBuffer( key );
		}, FLUSH_DEBOUNCE_MS );

		this.flushTimers.set( key, timer );
	}

	_flushBuffer( key ) {
		const buffers = this.buffers.get( key );
		if ( !buffers || buffers.length === 0 ) return;

		const [ projectID, folderIDPart ] = key.split( ':' );
		const folderID = folderIDPart === 'root' ? null : Number( folderIDPart );

		this.buffers.set( key, [] );
		this.pendingBatches.push( {
			projectID: Number( projectID ),
			folderID,
			entries: [ ...buffers ]
		} );

		this._processQueue();
	}

	_processQueue() {
		while ( this.inFlightCount < MAX_CONCURRENT_BATCHES && this.pendingBatches.length > 0 ) {
			const batch = this.pendingBatches.shift();
			this._sendBatch( batch );
			this.inFlightCount += 1;
		}
	}

	_finishBatch( projectID, succeeded, failed ) {
		this.store.dispatch( {
			type: CREATE_ASSETS_BATCH_FROM_DIRECT_UPLOAD_FINISH,
			payload: {
				succeeded: succeeded.map( ( { uploadID, assetID } ) => ( { uploadID, assetID } ) ),
				failed: failed.map( ( { uploadID, errorName } ) => ( { uploadID, errorName } ) )
			}
		} );

		this.onBatchComplete?.( {
			projectID,
			succeeded: succeeded.map( ( { parentUploadID } ) => ( { parentUploadID } ) ),
			failed: failed.map( ( { parentUploadID, errorName } ) => ( {
				error: { name: errorName },
				parentUploadID
			} ) )
		} );
	}

	_sendBatch( { projectID, folderID, entries } ) {
		const uploadIDs = entries.map( e => e.uploadID );

		this.store.dispatch( {
			type: CREATE_ASSETS_BATCH_FROM_DIRECT_UPLOAD_START,
			payload: { uploadIDs }
		} );

		const requestEntries = entries.map( ( { signedId } ) => ( { signedId } ) );
		const localFolderName = entries.find( e => e.localFolderName )?.localFolderName;

		this.assetSystem
			.createAssetsBatchFromDirectUpload( requestEntries, projectID, folderID, localFolderName )
			.then( ( { assets, errors } ) => {
				const failedIndices = new Set( errors.map( e => e.index ) );
				const errorByIndex = new Map( errors.map( e => [ e.index, e ] ) );
				let assetIndex = 0;

				const succeeded = [];
				const failed = [];

				entries.forEach( ( entry, index ) => {
					if ( failedIndices.has( index ) ) {
						const err = errorByIndex.get( index );
						failed.push( {
							uploadID: entry.uploadID,
							errorName: err.message,
							parentUploadID: entry.parentUploadID
						} );
					} else {
						const assetJson = assets[ assetIndex ];
						assetIndex += 1;
						QueryClientUpdater.createdOrUpdatedAsset( {
							assetJson: { ...assetJson, project_id: projectID },
							projectID,
							parentUploadsData: entry.parentUploadsData
						} );

						succeeded.push( {
							uploadID: entry.uploadID,
							parentUploadID: entry.parentUploadID,
							assetID: assetJson.id
						} );
					}
				} );

				this._finishBatch( projectID, succeeded, failed );
			} )
			.catch( ( error ) => {
				const errorName = error?.message ?? 'Batch request failed';
				const failed = entries.map( ( { uploadID, parentUploadID } ) => ( {
					uploadID,
					errorName,
					parentUploadID
				} ) );

				this._finishBatch( projectID, [], failed );
			} )
			.finally( () => {
				this.inFlightCount -= 1;
				this._processQueue();
			} );
	}
}
